import { useRef, useEffect } from "react";
import { Calendar, HeartPulse } from "lucide-react";
import { useTheme } from "../components/ThemeContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import ShinyText from "../components/reactbits/ShinyText";
import { RotatingWord } from "../components/RotatingWord";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { DemoRequestDialog } from "@/components/DemoRequestDialog";
import { PageHeader } from "../components/PageHeader";
import SEO from "../components/SEO";

// Healthcare page sections, in the order providers evaluate a platform:
// what it is -> how it works -> patient benefit -> organization value -> trust -> proof -> how to join
import VideoMarquee from "../components/healthcare/VideoMarquee";
import WhatIsQuilt from "../components/healthcare/WhatIsQuilt";
import HowItWorks from "../components/healthcare/HowItWorks";
import KeyFeatures from "../components/healthcare/KeyFeatures";
import SentimentSandbox from "../components/healthcare/SentimentSandbox";
import AdaptiveContent from "../components/healthcare/AdaptiveContent";
import QuiltFeatures from "../components/healthcare/QuiltFeatures";
import SafetyTrust from "../components/healthcare/SafetyTrust";
import ProductTour from "../components/healthcare/ProductTour";
import { EvidenceAndTestimonials, PartnerLogos } from "../components/healthcare/SocialProof";
import ClinicOnboarding from "../components/healthcare/ClinicOnboarding";
import StickyDemoCTA from "../components/healthcare/StickyDemoCTA";
import { secondaryButtonClass } from "../components/healthcare/buttonStyles";
import { evidenceStats, partners, testimonials } from "../config/providerProof";

gsap.registerPlugin(ScrollTrigger);

// Words that rotate through the hero headline: "A better way to ___ your patients between visits"
const HOOK_VERBS = ["engage", "nudge", "activate", "empower", "support", "assess", "retain", "reconnect with"];

// Hero buttons: equal width (up to 225px) when stacked on mobile, fixed widths side by side from sm up
const heroButtonSize = "h-[51px] w-full max-w-[225px] px-6 text-lg sm:max-w-none sm:px-8";

// Land the section's content (not its padding) just under the fixed header. offsetTop ignores the fade-in's translate.
const scrollToWhatIsQuilt = () => {
    const section = document.getElementById("platform");
    if (!section) return;
    let top = 0;
    for (let el: HTMLElement | null = section; el; el = el.offsetParent as HTMLElement | null) top += el.offsetTop;
    const inner = [...section.children].find((c) => (c as HTMLElement).offsetParent !== null) ?? section.firstElementChild;
    const padding = inner ? parseFloat(getComputedStyle(inner).paddingTop) || 0 : 0;
    const headerBottom = document.querySelector("header")?.getBoundingClientRect().bottom ?? 80;
    window.scrollTo({ top: top + padding - headerBottom - 16, behavior: "smooth" });
};

// Sections parked for later
const SHOW_HOW_IT_WORKS = false;
const SHOW_KEY_FEATURES = false;

const bandClass = "border-t border-slate-200 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-900/20";

export function Healthcare() {
    const { theme } = useTheme();
    const sectionRefs = useRef<HTMLElement[]>([]);

    useEffect(() => {
        // GSAP fade-in for each section; the context tears the triggers down when leaving the page
        const ctx = gsap.context(() => {
            sectionRefs.current.forEach((section) => {
                gsap.fromTo(
                    section,
                    { opacity: 0, y: 60 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: section,
                            start: "top 85%",
                            toggleActions: "play none none reverse",
                        },
                    }
                );
            });
        });
        return () => ctx.revert();
    }, []);

    const addToRefs = (el: HTMLElement | null) => {
        if (el && !sectionRefs.current.includes(el)) sectionRefs.current.push(el);
    };

    const navItems = [
        { label: "For You", href: "/" },
        { label: "For Providers", href: "/healthcare", selected: true },
    ];

    const hasEvidence = evidenceStats.length > 0 || testimonials.length > 0;
    const hasPartners = partners.length > 0;

    return (
        <div className={theme === "dark" ? "bg-transparent text-white" : "bg-transparent text-slate-900"}>
            <SEO
                title="Quilt for Healthcare | Adaptive Content & Patient Engagement"
                description="Empower your clinic with generative content pipelines that deliver personalized patient care plans, engagement tracking, and video insights."
                path="/healthcare"
            />
            <PageHeader
                navItems={navItems}
                customAction={
                    <>
                        <a href="https://web.medical.quilt.gg/" className={`text-sm px-1 mr-1 font-semibold transition-colors ${theme === 'dark'
                            ? "text-slate-300 hover:text-white"
                            : "text-slate-600 hover:text-slate-900"
                            }`}>
                            Clinic login
                        </a>
                        <DemoRequestDialog triggerText="Get Started" source="provider_get_started">
                            <Button className="btn-primary text-white">
                                Get Started
                            </Button>
                        </DemoRequestDialog>
                    </>
                }
                customActionMobile={
                    <>
                        <a href="https://web.medical.quilt.gg/" className={`text-lg text-center btn-secondary px-6 py-2.5 font-semibold transition-colors ${theme === 'dark'
                            ? "text-slate-300 hover:text-white"
                            : "text-slate-600 hover:text-slate-900"
                            }`}>
                            Clinic login
                        </a>
                        <DemoRequestDialog triggerText="Get Started" source="provider_get_started">
                            <Button className="btn-primary text-white text-lg px-6 py-3 h-auto items-center justify-center">
                                Get Started
                            </Button>
                        </DemoRequestDialog>
                    </>
                }
            />

            {/* 1. Hero: hook + auto-scrolling clips */}
            <section
                id="hero"
                ref={addToRefs}
                className="relative overflow-hidden pt-28 pb-6 md:pt-40 md:pb-16"
            >
                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-5xl mx-auto text-center">
                        <Badge
                            variant="outline"
                            className="mb-6 px-4 py-2 text-sm bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-500/30"
                        >
                            <HeartPulse className="w-4 h-4 mr-2 text-purple-500" />
                            <ShinyText text="Quilt For Care Providers" speed={4} className="font-medium" />
                        </Badge>

                        <h1
                            className={`text-[length:clamp(1.25rem,calc((100vw_-_2rem)/12.9),2.25rem)] md:text-6xl mb-8 leading-tight font-light ${theme === "dark" ? "text-white" : "text-slate-900"
                                }`}
                        >
                            {/* The rotating word is visual only; screen readers and crawlers get the full sentence */}
                            <span className="sr-only">A better way to engage your patients between visits</span>
                            <span aria-hidden="true" className="flex flex-col items-center">
                                <span>A better way to</span>
                                <RotatingWord
                                    words={HOOK_VERBS}
                                    className="my-1 md:my-2 px-2 font-semibold text-purple-500 dark:text-purple-400"
                                />
                                <span>your patients between visits</span>
                            </span>
                        </h1>

                        <p
                            className={`text-lg md:text-xl max-w-3xl mx-auto mb-10 leading-relaxed font-medium ${theme === "dark" ? "text-slate-400" : "text-slate-600"
                                }`}
                        >
                            Quilt bridges the gap between visits with daily personalized micro-experiences tailored to empower your patients and build lasting engagement with your practice
                        </p>

                        <div data-hero-cta className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center">
                            <DemoRequestDialog triggerText="Schedule a Demo" source="provider_schedule_demo">
                                <Button size="lg" className={`${heroButtonSize} sm:w-[290px] btn-primary text-white shadow-xl shadow-purple-500/20`}>
                                    <Calendar className="mr-2 w-5 h-5" />
                                    Schedule a Demo
                                </Button>
                            </DemoRequestDialog>
                            <Button
                                size="lg"
                                variant="outline"
                                onClick={scrollToWhatIsQuilt}
                                className={`${heroButtonSize} sm:w-[248px] ${secondaryButtonClass(theme === "dark")}`}
                            >
                                See how it works
                            </Button>
                        </div>
                    </div>
                </div>

                <VideoMarquee />
            </section>

            {/* 2. What the platform is */}
            <section id="platform" className={bandClass}>
                <WhatIsQuilt />
            </section>

            {/* 3. How it works + key features: hidden for now (they repeat the "What is Quilt?" cards); flip the flags to bring them back */}
            {SHOW_HOW_IT_WORKS && (
                <section id="how-it-works" ref={addToRefs}>
                    <HowItWorks />
                </section>
            )}

            {SHOW_KEY_FEATURES && (
                <section id="features" ref={addToRefs} className="px-4">
                    <KeyFeatures />
                </section>
            )}

            {/* 4. How it helps your patients */}
            <section id="patients" ref={addToRefs} className={`px-4 ${bandClass}`}>
                <SentimentSandbox />
            </section>

            <section ref={addToRefs} className="px-4">
                <AdaptiveContent />
            </section>

            {/* 5. How it helps your organization */}
            <section id="value" ref={addToRefs} className={`px-4 ${bandClass}`}>
                <QuiltFeatures />
            </section>

            {/* 5b. Inside Quilt Pro: live screens, handed over one by one as the page scrolls.
                No fade-in ref here: the section pins its screens, so it must not be transformed. */}
            <section id="product-tour" className="pb-10 md:pb-20">
                <ProductTour />
            </section>

            {/* 6. Safety, security & trust */}
            <section id="trust">
                <SafetyTrust />
            </section>

            {/* 7. Evidence, testimonials and partners (each hidden until real content is added in config/providerProof.ts) */}
            {hasEvidence && (
                <section id="evidence" ref={addToRefs} className={`px-4 ${bandClass}`}>
                    <EvidenceAndTestimonials />
                </section>
            )}

            {hasPartners && (
                <section id="partners" ref={addToRefs} className="px-4">
                    <PartnerLogos />
                </section>
            )}

            {/* 8. How to join + CTAs */}
            <section id="get-started" ref={addToRefs} className="px-4">
                <ClinicOnboarding />
            </section>

            <section
                id="contact"
                ref={addToRefs}
                className="py-10 md:py-24 border-t border-slate-200 dark:border-slate-800/50"
            >
                <div className="container mx-auto px-4">
                    <Card
                        className={`max-w-4xl mx-auto p-12 md:p-16 text-center border ${theme === "dark"
                            ? "bg-gradient-to-br from-slate-900 via-purple-950/20 to-slate-900 border-purple-500/30"
                            : "bg-gradient-to-br from-white via-purple-50 to-white border-purple-200"
                            }`}
                    >
                        <h2
                            className={`text-3xl md:text-4xl font-bold mb-6 ${theme === "dark" ? "text-white" : "text-slate-900"
                                }`}
                        >
                            Ready to Transform Patient Engagement?
                        </h2>
                        <p
                            className={`text-lg mb-8 max-w-2xl mx-auto ${theme === "dark" ? "text-slate-400" : "text-slate-600"
                                }`}
                        >
                            Join the healthcare providers who are already seeing measurable improvements in patient outcomes and revenue.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <DemoRequestDialog triggerText="Schedule a Demo" source="provider_schedule_demo">
                                <Button size="lg" className="btn-primary text-white text-lg px-8 rounded-full">
                                    <Calendar className="mr-2 w-5 h-5" />
                                    Schedule a Demo
                                </Button>
                            </DemoRequestDialog>
                        </div>
                    </Card>
                </div>
            </section>

            {/* FAQ Section */}
            <section
                id="faq"
                ref={addToRefs}
                className="py-10 md:py-24"
            >
                <div className="container mx-auto px-4 max-w-3xl">
                    <div className="text-center mb-6 md:mb-12">
                        <Badge variant="outline" className="mb-4 px-4 py-2 bg-slate-500/10 border-slate-500/30">
                            <ShinyText text="FAQ" speed={4} className="font-medium" />
                        </Badge>
                        <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
                            Frequently Asked Questions
                        </h2>
                    </div>

                    <Card className={`p-6 md:p-8 dark:bg-slate-900/50 bg-white border ${theme === "dark" ? "border-slate-800" : "border-slate-200"}`}>
                        <Accordion type="single" collapsible className="w-full">
                            {[
                                {
                                    question: "How much oversight do I actually have over the AI-generated content?",
                                    answer: "You maintain total clinical authority. Every batch of content is made available to your team for review, approval, or rejection before it reaches your patients."
                                },
                                {
                                    question: "How does the engine \"know\" what my patient needs?",
                                    answer: "Our platform cross-references behavioral signals — like swipe velocity and completion depth — with demographic information, clinical assessment responses, and subjective reporting to continuously learn and optimize for what engages a patient and drives improvement in their well-being with each new generation of content."
                                },
                                {
                                    question: "Will this create more administrative work for my team?",
                                    answer: "On the contrary, Quilt is designed to operate autonomously whether its generating content, deploying assessments, or nudging patient actions. Once you define your goals, Quilt handles the rest, only requiring your team for final approval and feedback."
                                },
                                {
                                    question: "How do we onboard our existing patient database?",
                                    answer: "Integration is seamless. Add individual patients in the “Patient Management” section of our web app or mass upload your patient list with our secure API. Patients will receive unique invite codes linked to your clinic and a download link to our mobile app to sign-up."
                                }
                            ].map((faq, index) => (
                                <AccordionItem key={index} value={`item-${index}`} className={theme === "dark" ? "border-slate-800" : "border-slate-200"}>
                                    <AccordionTrigger className={`text-left text-lg font-medium hover:no-underline ${theme === "dark" ? "text-slate-200 hover:text-white" : "text-slate-800 hover:text-slate-900"}`}>
                                        {faq.question}
                                    </AccordionTrigger>
                                    <AccordionContent className={`text-base leading-relaxed ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                                        {faq.answer}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Card>
                </div>
            </section>
            <StickyDemoCTA />
        </div>
    );
}

export default Healthcare;
